import {inject, Injectable} from '@angular/core';
import {System} from "./definitions/system";
import {MetaField} from "./definitions/meta-field";
import {LoggerService} from "./logger.service";
import {FieldParameters} from "./definitions/field-parameters";

import {FormGroupParams} from "./definitions/form-group.params";

@Injectable({
  providedIn: 'root'
})
export class FieldKeyService {
  private readonly logger: LoggerService = inject(LoggerService);

  setFieldKey(field: MetaField, key: string) {
    field.key = key;
  }

  getInlineArrayItemId(fieldParameters: FieldParameters) {
    let key: string;
    if (fieldParameters.parentIndex === undefined) {
      key = fieldParameters.field.key + '[{index1}]';
    } else if (fieldParameters.field.key.includes('{index2}')) {
      // Special handling for multiple field value operations
      key = fieldParameters.field.key + '[{index3}]';
    } else {
      key = fieldParameters.field.key + '[{index2}]';
    }
    const metaField: MetaField = {} as MetaField;
    this.setFieldKey(metaField, key)
    return this.getFieldKeyWhileDrawingInputs(
      metaField, fieldParameters.index, fieldParameters.parentIndex, fieldParameters.grandparentIndex);
  }

  generateSubfieldKey(fieldParameters: FieldParameters, subFieldMeta: MetaField, curIndex: number): string {
    // Generate a subfield key based on parent key, array index and subfield name;
    let subFieldKey: string;
    if (fieldParameters.parentIndex === undefined) {
      subFieldKey = subFieldMeta.key.replace('{index1}', curIndex.toString());
    } else {
      subFieldKey = subFieldMeta.key.replace('{index1}',
        fieldParameters.parentIndex.toString()).replace('{index2}', curIndex.toString());
    }
    return subFieldKey
  }

  // This is method must be used for getting field key during generation of field inputs as the indexes
  // are generated differently than getting field keys when generating form controls
  getFieldKeyWhileDrawingInputs(metaField: MetaField, index?: number, parentIndex?: number, grandparentIndex?: number): string {
    let fieldKey: string
    if (!metaField) {
      this.logger.warn('Field is missing!');
      return null;
    }
    if (metaField.key) {
      if (metaField.key.includes('{index3}') && (grandparentIndex === undefined || grandparentIndex === null)) {
        this.logger.warn('Field key requires grandparent index but it was not provided: ' + metaField.key);
      }
      if (index === undefined) {
        fieldKey = metaField.key;
      } else if (parentIndex === undefined) {
        fieldKey = this.getFieldKey(metaField, index);
      } else if (grandparentIndex === undefined) {
        fieldKey = this.getFieldKey(metaField, parentIndex, index);
      } else {
        fieldKey = this.getFieldKey(metaField, parentIndex, index, '', grandparentIndex);
      }
    } else {
      fieldKey = metaField.name;
      if (metaField.path) {
        fieldKey = `${metaField.path}${System.keySep}${metaField.name}`;
      }
    }
    return fieldKey;
  }

  // Get a field key in which inline array index names are replaced with the actual indexes
  getFieldKey(metaField: MetaField, parentIndex?: number, index?: number, fieldName?: string, grandparentIndex?: number): string {
    let fieldKey = metaField.key;
    if (!fieldKey) {
      this.logger.warn(`Key not generated for field ${metaField.name || fieldName}`);
      return null;
    }
    if (fieldKey.includes('{index3}')) {
      if (!grandparentIndex && grandparentIndex !== 0) {
        this.logger.warn('Field key requires grandparent index but it was not provided: ' + fieldKey);
      }
      // Special handling for multiple value operations with grandparent index
      fieldKey = fieldKey.replace('{index1}', grandparentIndex.toString());
      fieldKey = fieldKey.replace('{index2}', parentIndex.toString());
      fieldKey = fieldKey.replace('{index3}', index.toString());
      return fieldKey;
    }
    if (fieldKey.includes('{index2}')) {
      if (!parentIndex && parentIndex !== 0) {
        this.logger.warn('Field key requires parent index but it was not provided: ' + fieldKey);
      }
      fieldKey = fieldKey.replace('{index2}', index.toString());
      fieldKey = fieldKey.replace('{index1}', parentIndex.toString());
      return fieldKey;
    }
    if (fieldKey.includes('{index1}')) {
      if (parentIndex || parentIndex === 0) {
        fieldKey = fieldKey.replace('{index1}', parentIndex.toString());
        if (index || index === 0) {
          fieldKey = fieldKey.replace('{index2}', index.toString());
        }
      } else if (index !== undefined && index !== null) {
        fieldKey = fieldKey.replace('{index1}', index.toString());
      } else if (grandparentIndex || grandparentIndex === 0) {
        fieldKey = fieldKey.replace('{index1}', grandparentIndex.toString());
      } else {
        this.logger.warn('Field key requires index parameters but it was not provided: ' + fieldKey);
      }
    }
    if (fieldKey.includes('{')) {
      this.logger.warn('Key not properly generated: ' + fieldKey + ' parentIndex: ' + parentIndex + ' index: ' +
        index + ' key ' + metaField.key + ' grandparentIndex: ' + grandparentIndex);
    }
    if (fieldName) {
      const parentKey = this.getKeysParentKey(fieldKey);
      if (parentKey) {
        fieldKey = `${parentKey}${System.keySep}${fieldName}`;
      } else {
        fieldKey = fieldName;
      }
    }
    return fieldKey;
  }

  // Recursively generate field key based on parent field keys
  generateFieldKey(metaField: MetaField, parentKey: string): string {
    let res = metaField.key;
    if (!res) {
      res = metaField.name;
      const parentName = metaField.parent_name;
      if (parentName && (!parentKey || !this.parentNameExists(parentKey, parentName))) {
        res = `${metaField.parent_name}${System.keySep}${res}`;
      }
      if (parentKey) {
        res = `${parentKey}${System.keySep}${res}`;
      }
      this.setFieldKey(metaField, res);
    } else if (res === parentKey && !res.includes(metaField.name)) {
      res = `${parentKey}${System.keySep}${metaField.name}`;
    }
    return res;
  }

  getKeysParentKey(key: string) {
    const lastSep = key.lastIndexOf(System.keySep);
    let res = '';
    if (lastSep !== -1) {
      res = key.substring(0, lastSep);
    }
    return res;
  }

  getIndexesFromFormGroupParams(params: FormGroupParams, index: number) {
    const fieldKey = this.generateFieldKey(params.field, params.parentKey);
    let indexKey: string;
    if (fieldKey.includes('{index2}') && (params.grandparentIndex || params.grandparentIndex === 0)) {
      indexKey = fieldKey + '[{index3}]';
    } else if (fieldKey.includes('{index1}') && (params.parentIndex || params.parentIndex === 0)) {
      indexKey = fieldKey + '[{index2}]';
    } else if (!fieldKey.includes('{index1}') && (params.index || params.index === 0 || index || index === 0)) {
      indexKey = fieldKey + '[{index1}]';
    } else {
      indexKey = fieldKey;
    }
    return {
      indexKey: indexKey,
      grandparentIndex: params.grandparentIndex,
      parentIndex: params.parentIndex,
      index: index
    };
  }
  private parentNameExists(parentKey: string, parentName: string) {
    let res = false;
    const existsIndex = parentKey.indexOf(parentName);
    if (existsIndex !== -1) {
      if (existsIndex === 0 || parentKey.endsWith(parentName) || parentKey.includes(`${System.keySep}${parentName}${System.keySep}`) || parentKey.includes(`${System.keySep}${parentName}[`)) {
        res = true;
      }
    }
    return res;
  }


}
